import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { useMoneyFormatter } from "@/lib/currency";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import DateRange from "@/components/DateRange";
import { toast } from "sonner";

export default function AccountingPage() {
  const [tab, setTab] = useState("trial-balance");
  return (
    <div data-testid="accounting-page">
      <PageHeader title="Accounting" subtitle="Chart of accounts · Journal · Trial balance" />
      <div className="flex gap-2 mb-6 border-b border-border">
        {[
          { k: "trial-balance", label: "Trial Balance" },
          { k: "journal", label: "Journal" },
          { k: "chart", label: "Chart of Accounts" },
          { k: "ledger", label: "General Ledger" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${tab === t.k ? "border-forest text-forest" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-${t.k}`}>{t.label}</button>
        ))}
      </div>
      {tab === "trial-balance" && <TrialBalance />}
      {tab === "journal" && <Journal />}
      {tab === "chart" && <ChartOfAccounts />}
      {tab === "ledger" && <GeneralLedger />}
    </div>
  );
}

function TrialBalance() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const fmt = useMoneyFormatter();
  useEffect(() => {
    const qs = [range.from && `date_from=${encodeURIComponent(range.from)}`, range.to && `date_to=${encodeURIComponent(range.to)}`].filter(Boolean).join("&");
    api.get(`/accounting/trial-balance${qs ? "?" + qs : ""}`).then(({ data }) => setData(data));
  }, [range.from, range.to]);
  if (!data) return <div className="label">Loading…</div>;
  return (
    <Section title="Trial Balance" testId="trial-balance"
      actions={<a className="btn-ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/export/trial-balance?format=pdf`} target="_blank" rel="noreferrer" data-testid="download-tb-pdf">Download PDF</a>}>
      <div className="mb-6"><DateRange value={range} onChange={setRange} testId="tb-range" /></div>
      {data.rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No entries posted in this range.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left">
            <th className="label py-2">Code</th><th className="label py-2">Account</th><th className="label py-2">Type</th>
            <th className="label py-2 text-right">Debit</th><th className="label py-2 text-right">Credit</th><th className="label py-2 text-right">Balance</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.code} className="border-t border-border">
                <td className="py-2.5 font-mono text-xs">{r.code}</td>
                <td className="py-2.5">{r.name}</td>
                <td className="py-2.5"><Pill variant="info">{r.type}</Pill></td>
                <td className="py-2.5 text-right tabular-nums">{fmt(r.debit)}</td>
                <td className="py-2.5 text-right tabular-nums">{fmt(r.credit)}</td>
                <td className="py-2.5 text-right tabular-nums font-medium">{fmt(r.balance)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-foreground font-semibold">
              <td colSpan={3} className="py-3 text-right">TOTAL</td>
              <td className="py-3 text-right tabular-nums">{fmt(data.total_debit)}</td>
              <td className="py-3 text-right tabular-nums">{fmt(data.total_credit)}</td>
              <td className="py-3 text-right tabular-nums">{fmt(data.total_debit - data.total_credit)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Journal() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [items, setItems] = useState([]);
  const fmt = useMoneyFormatter();
  useEffect(() => {
    const qs = [range.from && `date_from=${encodeURIComponent(range.from)}`, range.to && `date_to=${encodeURIComponent(range.to)}`].filter(Boolean).join("&");
    api.get(`/accounting/journal${qs ? "?" + qs : ""}`).then(({ data }) => setItems(data));
  }, [range.from, range.to]);
  return (
    <Section title="Recent Journal Entries" testId="journal-section">
      <div className="mb-6"><DateRange value={range} onChange={setRange} testId="journal-range" /></div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No entries in this range.</div>
      ) : items.map((e) => (
        <div key={e.id} className="border border-border rounded-md p-4 mb-3" data-testid={`journal-${e.id}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="text-xs text-muted-foreground">{fmtDate(e.date)}</div>
              <div className="font-medium">{e.description}</div>
              {e.source_type && <Pill variant="info">{e.source_type}</Pill>}
            </div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {e.lines.map((l, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1.5 font-mono text-xs w-20">{l.account_code}</td>
                  <td className="py-1.5">{l.account_name}</td>
                  <td className="py-1.5 text-right tabular-nums w-32">{l.debit > 0 ? fmt(l.debit) : ""}</td>
                  <td className="py-1.5 text-right tabular-nums w-32 text-muted-foreground">{l.credit > 0 ? fmt(l.credit) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Section>
  );
}

function ChartOfAccounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "ASSET" });
  const load = () => api.get("/accounting/chart").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounting/chart", form);
      toast.success("Account created");
      setOpen(false); setForm({ code: "", name: "", type: "ASSET" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";
  return (
    <Section title={`Chart of Accounts (${items.length})`} testId="chart-section"
      actions={<button onClick={() => setOpen(!open)} className="btn-ghost" data-testid="add-account-btn">{open ? "Cancel" : "Add account"}</button>}>
      {open && (
        <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 mb-6 p-4 border border-border rounded-md">
          <input required placeholder="Code (e.g. 1200)" className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="acc-code" />
          <input required placeholder="Name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="acc-name" />
          <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="acc-type">
            <option>ASSET</option><option>LIABILITY</option><option>EQUITY</option><option>INCOME</option><option>EXPENSE</option>
          </select>
          <div className="md:col-span-3 flex justify-end"><button type="submit" className="btn-primary" data-testid="acc-submit-btn">Save</button></div>
        </form>
      )}
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No accounts yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left">
            <th className="label py-2">Code</th><th className="label py-2">Name</th><th className="label py-2">Type</th><th className="label py-2">System</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-border">
                <td className="py-2.5 font-mono text-xs">{a.code}</td>
                <td className="py-2.5 font-medium">{a.name}</td>
                <td className="py-2.5"><Pill variant="info">{a.type}</Pill></td>
                <td className="py-2.5"><Pill variant={a.is_system ? "default" : "success"}>{a.is_system ? "System" : "Custom"}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function GeneralLedger() {
  const [accounts, setAccounts] = useState([]);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const fmt = useMoneyFormatter();
  useEffect(() => { api.get("/accounting/chart").then(({ data }) => { setAccounts(data); if (data[0]) setCode(data[0].code); }); }, []);
  useEffect(() => {
    if (code) api.get(`/accounting/general-ledger/${code}`).then(({ data }) => setData(data));
  }, [code]);
  const inputCls = "w-full md:w-72 bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";
  return (
    <Section title="General Ledger" testId="ledger-section">
      <div className="mb-4">
        <label className="label block mb-1">Account</label>
        <select className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} data-testid="ledger-account">
          {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
        </select>
      </div>
      {!data || data.rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No movements on this account.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left">
            <th className="label py-2">Date</th><th className="label py-2">Description</th>
            <th className="label py-2 text-right">Debit</th><th className="label py-2 text-right">Credit</th><th className="label py-2 text-right">Balance</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="py-2.5">{fmtDate(r.date)}</td>
                <td className="py-2.5">{r.description}</td>
                <td className="py-2.5 text-right tabular-nums">{r.debit > 0 ? fmt(r.debit) : ""}</td>
                <td className="py-2.5 text-right tabular-nums">{r.credit > 0 ? fmt(r.credit) : ""}</td>
                <td className="py-2.5 text-right tabular-nums font-medium">{fmt(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}
